import React, { Suspense, lazy } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "./contexts/AuthContext";
import { I18nProvider } from "./contexts/I18nContext";
import Layout from "./components/Layout";
import ProtectedRoute from "./components/ProtectedRoute";
import ScrollToTop from "./components/ScrollToTop";

// Safety net: redirect HTTP → HTTPS in production (server-level redirect is primary)
if (
  typeof window !== "undefined" &&
  window.location.protocol === "http:" &&
  window.location.hostname !== "localhost" &&
  window.location.hostname !== "127.0.0.1"
) {
  window.location.replace(
    "https://" + window.location.host + window.location.pathname + window.location.search + window.location.hash
  );
}

// ── Eagerly loaded (critical path — shown on first visit) ─────────────────────
import Landing from "./pages/Landing";
import Courses from "./pages/Courses";
import CourseDetail from "./pages/CourseDetail";
import Login from "./pages/Login";
import Register from "./pages/Register";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import VerifyEmail from "./pages/VerifyEmail";
import NotFound from "./pages/NotFound";

// ── Lazy loaded (split into separate chunks, loaded on demand) ────────────────
const About             = lazy(() => import("./pages/About"));
const Playground        = lazy(() => import("./pages/Playground"));
const Profile           = lazy(() => import("./pages/Profile"));
const PaymentSuccess    = lazy(() => import("./pages/PaymentSuccess"));
const StudentDashboard  = lazy(() => import("./pages/StudentDashboard"));
const TeacherDashboard  = lazy(() => import("./pages/TeacherDashboard"));
const AdminDashboard    = lazy(() => import("./pages/AdminDashboard"));
const AdminSupport      = lazy(() => import("./pages/AdminSupport"));
const QuizPage          = lazy(() => import("./pages/QuizPage"));
const AssignmentsPage   = lazy(() => import("./pages/AssignmentsPage"));
const CertificatePrint  = lazy(() => import("./pages/CertificatePrint"));
const Classroom         = lazy(() => import("./pages/Classroom"));
const CorporateTraining = lazy(() => import("./pages/CorporateTraining"));
const CareerServices    = lazy(() => import("./pages/CareerServices"));
const VerifyCertificate = lazy(() => import("./pages/VerifyCertificate"));
const Blog              = lazy(() => import("./pages/Blog"));
const KidsAcademy       = lazy(() => import("./pages/KidsAcademy"));
const KidsPlayground    = lazy(() => import("./pages/KidsPlayground"));
const TeachWithUs       = lazy(() => import("./pages/TeachWithUs"));

export default function App() {
  return (
    <I18nProvider>
      <AuthProvider>
        <BrowserRouter>
          <ScrollToTop />
          <Layout>
            <Suspense fallback={<div className="min-h-screen" aria-busy="true" aria-label="Loading…" />}>
            <Routes>
              <Route path="/" element={<Landing />} />
              <Route path="/courses" element={<Courses />} />
              <Route path="/courses/:id" element={<CourseDetail />} />
              <Route path="/about" element={<About />} />
              <Route path="/corporate" element={<CorporateTraining />} />
              <Route path="/career" element={<CareerServices />} />
              <Route path="/verify-certificate" element={<VerifyCertificate />} />
              <Route path="/blog" element={<Blog />} />
              <Route path="/kids-academy" element={<KidsAcademy />} />
              <Route path="/kids-playground" element={<KidsPlayground />} />
              <Route path="/teach" element={<TeachWithUs />} />
              <Route path="/playground" element={<Playground />} />
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/verify-email" element={<VerifyEmail />} />
              <Route
                path="/profile"
                element={
                  <ProtectedRoute>
                    <Profile />
                  </ProtectedRoute>
                }
              />
              <Route path="/payment-success" element={<PaymentSuccess />} />
              <Route path="/courses/:courseId/quiz" element={<QuizPage />} />
              <Route path="/courses/:courseId/assignments" element={<AssignmentsPage />} />
              <Route path="/certificate/:certId/print" element={<CertificatePrint />} />
              <Route path="/classroom/:appointmentId" element={<Classroom />} />
              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute role="student">
                    <StudentDashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/teacher"
                element={
                  <ProtectedRoute role="teacher">
                    <TeacherDashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin"
                element={
                  <ProtectedRoute role={["admin", "sub_admin"]}>
                    <AdminDashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/support"
                element={
                  <ProtectedRoute role={["admin", "sub_admin"]}>
                    <AdminSupport />
                  </ProtectedRoute>
                }
              />
              <Route path="*" element={<NotFound />} />
            </Routes>
            </Suspense>
          </Layout>
        </BrowserRouter>
      </AuthProvider>
    </I18nProvider>
  );
}
